<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>
        {{ $t("marcosComisiones.titulo") }}
      </h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearMarcoComision()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid
      v-bind="getDefaultGridProperties()"
      :data-source="gridSource"
      ref="grid"
      class="dx-card wide-card grid-marcos-comisiones"
      @content-ready="onContentReady"
    >
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Denominacion" :caption="$t('marcosComisiones.denominacion')"></DxColumn>
      <DxColumn data-field="UsuarioCreacion" :caption="$t('marcosComisiones.usuarioCreacion')"></DxColumn>
      <DxColumn data-field="FechaCreacion" :caption="$t('marcosComisiones.fechaCreacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="UsuarioModificacion" :caption="$t('marcosComisiones.usuarioModificacion')"></DxColumn>
      <DxColumn data-field="FechaModificacion" :caption="$t('marcosComisiones.fechaModificacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>

      <DxColumn
        :caption="$t('marcosComisiones.ui.acciones')"
        type="buttons"
        :fixed="true"
        fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('marcosComisiones.ui.duplicar'),
            icon: 'fa-solid fa-clone text-success',
            onClick: (e: any) => duplicar(e.row),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('marcosComisiones.ui.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => editar(e.row),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('marcosComisiones.ui.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row),
            disabled: (options: any) => soloHayUnElemento,
          },
        ]"
      />
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { type DeleteMarcoComisionesModel, type DXLoadOptions } from "@/.shared/.api";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import MarcoComisionesForm from "./MarcoComisionesForm.vue";
import { CreateMarcoComisiones } from "./models/CreateMarcoComisiones";
import MarcoComisionesReasignacionForm from "./MarcoComisionesReasignacionForm.vue";

const api = useApi();
const intl = useIntl();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const totalCount = ref(0);

const soloHayUnElemento = computed(() => {
  return totalCount.value === 1;
});

const onContentReady = () => {
  const instance = grid.value?.instance;
  if (instance) {
    totalCount.value = instance.totalCount();
  }
};

onMounted(async () => {
  setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getMarcosComisionesForGrid", { showLoading: false }, q);
    },
  });
};

const editar = (data: any) => {
  let title = `${intl.i18n.t("marcosComisiones.edicion.titulo")}`;
  modal.showModal(MarcoComisionesForm, { model: data.data, isEditMode: true }, { title: title, width: 500, height: 250 }).then((value) => {
    if (value == messageBoxResult.ok) {
      grid.value?.instance?.refresh();
    }
  });
};

const borrar = async (data: any) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("marcosComisiones.ui.borrar_pregunta"),
    intl.i18n.t("marcosComisiones.ui.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok) {
    modal
      .showModal(
        MarcoComisionesReasignacionForm,
        { idMarcoComisiones: data.data.Id },
        { title: intl.i18n.t("marcosComisiones.ui.titulo_reasignacion"), width: 450, height: 250 },
      )
      .then(async (value) => {
        if (value != messageBoxResult.cancel) {
          await api
            .callStandardApi("deleteMarcoComisiones", { showLoading: true }, data.data.Id, { IdMarcoReasignacion: value } as DeleteMarcoComisionesModel)
            .then(() => {
              toastSuccess(intl.i18n.t("marcosComisiones.eliminacion_exito"));
              grid.value?.instance?.refresh();
            })
            .catch(() => toastError(intl.i18n.t("marcosComisiones.eliminacion_fallo")));
        }
      });
  }
};

const duplicar = async (data: any) => {
  await api
    .callStandardApi("duplicateMarcoComisiones", { showLoading: true }, data.data.Id)
    .then(() => {
      toastSuccess(intl.i18n.t("marcosComisiones.duplicacion_exito"));
      grid.value?.instance?.refresh();
    })
    .catch(() => toastError(intl.i18n.t("marcosComisiones.duplicacion_fallo")));
};

const crearMarcoComision = () => {
  let title = `${intl.i18n.t("marcosComisiones.creacion.titulo")}`;
  modal.showModal(MarcoComisionesForm, { model: new CreateMarcoComisiones(), isEditMode: false }, { title: title, width: 500, height: 250 }).then((value) => {
    if (value == messageBoxResult.ok) {
      grid.value?.instance?.refresh();
    }
  });
};
</script>
